/**
 * Helpers de aplicación del branding al document (CSS vars + favicon).
 *
 * El shape canónico del branding (`TenantBrandingLocal`) vive en
 * `src/api/types/TenantLocal.ts` (es del dominio API). Aquí lo re-exportamos
 * para que consumers existentes del path `src/lib/theme` no rompan, pero la
 * source of truth del tipo es el `TenantLocal.ts`.
 */
import type { TenantBrandingLocal } from '../api/types/TenantLocal';
export type { TenantBrandingLocal };

type BrandingRequiredKey = 'primaryColor' | 'secondaryColor' | 'accentColor' | 'logoUrl';

const BRAND_VAR_MAP: Array<[BrandingRequiredKey, string]> = [
    ['primaryColor', '--brand-primary'],
    ['secondaryColor', '--brand-secondary'],
    ['accentColor', '--brand-accent'],
    ['logoUrl', '--brand-logo-url'],
];

// WCAG: foreground (color de texto) que contrasta con un fondo HSL space-separated.
// L > 55% → fondo claro → texto oscuro; L ≤ 55% → fondo oscuro → texto blanco.
function wcagForeground(hsl: string): string {
    const m = hsl.match(/(\d+(?:\.\d+)?)%\s*$/);
    const l = m ? parseFloat(m[1]) : 50;
    return l > 55 ? '222.2 84% 4.9%' : '210 40% 98%';
}

// TD-WEBAPP-007: formato HSL del backend a confirmar. Ver TECH_DEBT.md.
export function applyBrandingToDocument(branding: TenantBrandingLocal): void {
    const root = document.documentElement;
    for (const [key, cssVar] of BRAND_VAR_MAP) {
        const value = branding[key];
        if (!value) continue;
        const formatted = cssVar === '--brand-logo-url' ? `url(${value})` : value;
        root.style.setProperty(cssVar, formatted);
    }
    // Foreground calculado por contraste para primary y secondary (texto sobre esos fondos).
    root.style.setProperty('--primary-foreground', wcagForeground(branding.primaryColor));
    root.style.setProperty('--secondary-foreground', wcagForeground(branding.secondaryColor));
    // DEC-020: foreground legible sobre el color de acento de marca (texto/icono de acción).
    root.style.setProperty('--brand-accent-foreground', wcagForeground(branding.accentColor));
    // Favicon = el faviconUrl dedicado si existe, si no el LOGO del tenant (el editor hoy solo
    // gestiona logoUrl, así que el favicon sigue al logo). El logo puede no ser SVG, por eso
    // quitamos el `type` para que el browser infiera el formato del archivo real.
    const favicon = branding.faviconUrl ?? branding.logoUrl;
    if (favicon) {
        const link = document.querySelector("link[rel='icon']");
        if (link instanceof HTMLLinkElement) {
            link.href = favicon;
            link.removeAttribute('type');
        }
    }
}

export function clearBrandingFromDocument(): void {
    const root = document.documentElement;
    for (const [, cssVar] of BRAND_VAR_MAP) {
        root.style.removeProperty(cssVar);
    }
    root.style.removeProperty('--primary-foreground');
    root.style.removeProperty('--secondary-foreground');
    root.style.removeProperty('--brand-accent-foreground');
    // SIN branding (logout / tenant sin branding custom) → SIN favicon: no mostramos el logo
    // de Fiado. Un SVG transparente vacío "consume" el favicon sin pintar nada en la pestaña.
    const link = document.querySelector("link[rel='icon']");
    if (link instanceof HTMLLinkElement) {
        link.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'/>";
        link.setAttribute('type', 'image/svg+xml');
    }
}
